import { isUiMockMode } from "@/config/features";
import { env } from "@/config/env";
import { getAccountId } from "@/lib/auth/account";
import { StudioApiError } from "@/types/api";

export type InvoiceDocumentDownloadKind = "invoice_pdf" | "receipt";

function normalizeBaseUrl(base: string): string {
  return base.replace(/\/$/, "");
}

function buildDownloadPath(
  invoiceId: string,
  documentType: InvoiceDocumentDownloadKind,
  hints?: { pdfUrl?: string | null; invoiceNumber?: string | null },
): string {
  const params = new URLSearchParams({ document_type: documentType });
  if (documentType === "invoice_pdf") {
    if (hints?.pdfUrl) {
      params.set("pdf_url", hints.pdfUrl);
    }
    if (hints?.invoiceNumber) {
      params.set("invoice_number", hints.invoiceNumber);
    }
  }
  return `/invoices/${encodeURIComponent(invoiceId)}/pdf?${params.toString()}`;
}

function invoicePdfFallbackFileName(
  invoiceId: string,
  invoiceNumber?: string | null,
): string {
  const number = invoiceNumber?.trim();
  return number ? `Invoice-${number}.pdf` : `Invoice-${invoiceId}.pdf`;
}

function receiptPdfFallbackFileName(invoiceId: string): string {
  return `Receipt-${invoiceId}.pdf`;
}

function creditNotePdfFallbackFileName(
  creditNoteId: string,
  creditNoteNumber?: string | null,
): string {
  const number = creditNoteNumber?.trim();
  return number ? `CreditNote-${number}.pdf` : `CreditNote-${creditNoteId}.pdf`;
}

function triggerBlobDownload(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.rel = "noopener";
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

/** Cookie session: browser-native download (streams; download bar appears immediately). */
function triggerNativeDownload(path: string): void {
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.src = `${normalizeBaseUrl(env.apiBaseUrl)}${path}`;
  document.body.appendChild(iframe);
  window.setTimeout(() => iframe.remove(), 120_000);
}

async function fetchAndSaveBlob(
  path: string,
  fallbackFileName: string,
  accountId: string,
): Promise<void> {
  const url = `${normalizeBaseUrl(env.apiBaseUrl)}${path}`;
  const res = await fetch(url, {
    headers: {
      Accept: "application/pdf",
      "X-Stripe-Account-Id": accountId,
    },
    credentials: "include",
  });

  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { message?: string; code?: string };
      message = body.message ?? message;
      throw new StudioApiError(body.code ?? "UNKNOWN", message, res.status);
    } catch (e) {
      if (e instanceof StudioApiError) throw e;
      throw new StudioApiError("UNKNOWN", message, res.status);
    }
  }

  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const fileName = match?.[1] ?? fallbackFileName;
  triggerBlobDownload(blob, fileName);
}

async function downloadBinary(
  path: string,
  fallbackFileName: string,
): Promise<void> {
  if (isUiMockMode()) {
    throw new StudioApiError("MOCK_MODE", "Direct download is not available in mock mode");
  }

  const accountId = getAccountId();
  if (accountId?.startsWith("acct_")) {
    await fetchAndSaveBlob(path, fallbackFileName, accountId);
    return;
  }

  triggerNativeDownload(path);
}

export async function downloadInvoicePdfDirect(
  invoiceId: string,
  documentType: InvoiceDocumentDownloadKind = "invoice_pdf",
  hints?: { pdfUrl?: string | null; invoiceNumber?: string | null },
): Promise<void> {
  const fallbackFileName =
    documentType === "receipt"
      ? receiptPdfFallbackFileName(invoiceId)
      : invoicePdfFallbackFileName(invoiceId, hints?.invoiceNumber);
  await downloadBinary(
    buildDownloadPath(invoiceId, documentType, hints),
    fallbackFileName,
  );
}

export async function downloadCreditNotePdfDirect(
  creditNoteId: string,
  hints?: { creditNoteNumber?: string | null },
): Promise<void> {
  await downloadBinary(
    `/credit-notes/${encodeURIComponent(creditNoteId)}/pdf`,
    creditNotePdfFallbackFileName(creditNoteId, hints?.creditNoteNumber),
  );
}
